/**
 * Overview tab of the pilot dashboard.
 *
 * KPI tiles + time-series + source/campaign breakdown + funnel. Reads from
 * pilot_aggregates via the pilotDashboardOverview query.
 */

import React, {useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {useQuery} from '@apollo/client';
import {PILOT_DASHBOARD_OVERVIEW} from '../../graphql/pilotDashboardQueries';
import PilotTimeSeriesChart from './PilotTimeSeriesChart';
import PilotBreakdownChart from './PilotBreakdownChart';
import PilotFunnelChart from './PilotFunnelChart';

const STAGE_LABELS: Record<string, string> = {
  VISITED: 'Visited',
  ENGAGED_SIMULATOR: 'Engaged with Operations Simulator',
  ENGAGED_CALCULATOR: 'Engaged with ROI Calculator',
  OPENED_DIALOG: 'Opened lead dialog',
  SUBMITTED_LEAD: 'Submitted lead',
};

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function daysAgo(n: number): Date {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d;
}

interface KpiTile {
  label: string;
  value: string;
  hint?: string;
}

export default function PilotDashboardOverview() {
  const [startDate, setStartDate] = useState(isoDate(daysAgo(29)));
  const [endDate, setEndDate] = useState(isoDate(new Date()));

  const {data, loading, error} = useQuery(PILOT_DASHBOARD_OVERVIEW, {
    variables: {range: {startDate, endDate}},
    fetchPolicy: 'cache-and-network',
  });

  const overview = data?.pilotDashboardOverview;

  const tiles: KpiTile[] = useMemo(() => {
    const k = overview?.kpi;
    return [
      {
        label: 'New visitors',
        value: k ? k.visitors.toLocaleString() : '—',
      },
      {
        label: 'Sessions',
        value: k ? k.sessions.toLocaleString() : '—',
      },
      {
        label: 'Events',
        value: k ? k.events.toLocaleString() : '—',
      },
      {
        label: 'Leads',
        value: k ? k.leads.toLocaleString() : '—',
      },
      {
        label: 'Conversion rate',
        value: k ? `${(k.conversionRate * 100).toFixed(1)}%` : '—',
        hint: 'leads / new visitors',
      },
    ];
  }, [overview]);

  const isForbidden = (() => {
    if (!error) return false;
    const codes = error.graphQLErrors.map((e) => e.extensions?.code);
    return codes.includes('FORBIDDEN');
  })();

  return (
    <Box>
      <Stack
        direction={{xs: 'column', md: 'row'}}
        spacing={2}
        justifyContent="space-between"
        alignItems={{xs: 'stretch', md: 'flex-end'}}
        sx={{mb: 3}}
      >
        <Box>
          <Typography variant="h6" sx={{fontWeight: 600}}>
            Pilot landing performance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aggregated from pilot_aggregates. Updated as visits and leads come in.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{shrink: true}}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{shrink: true}}
          />
        </Stack>
      </Stack>

      {isForbidden && (
        <Alert severity="error" sx={{mb: 2}}>
          You don't have platform-admin permissions. This dashboard is
          restricted to operators with the <code>platformAdmin</code>{' '}
          custom claim or an entry in{' '}
          <code>PLANAPUS_PLATFORM_ADMIN_USER_IDS</code>.
        </Alert>
      )}
      {error && !isForbidden && (
        <Alert severity="error" sx={{mb: 2}}>
          Failed to load dashboard: {error.message}
        </Alert>
      )}

      <Stack
        direction={{xs: 'column', md: 'row'}}
        spacing={2}
        sx={{mb: 3}}
      >
        {tiles.map((t) => (
          <Paper
            key={t.label}
            variant="outlined"
            sx={{flex: 1, p: 2.5}}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{display: 'block', mb: 0.5}}
            >
              {t.label}
            </Typography>
            <Typography variant="h5" sx={{fontWeight: 600}}>
              {loading && !overview ? '…' : t.value}
            </Typography>
            {t.hint && (
              <Typography variant="caption" color="text.secondary">
                {t.hint}
              </Typography>
            )}
          </Paper>
        ))}
      </Stack>

      <Stack direction={{xs: 'column', lg: 'row'}} spacing={2}>
        <Paper variant="outlined" sx={{flex: 2, p: 2.5}}>
          <Typography
            variant="subtitle2"
            sx={{fontWeight: 600, mb: 2}}
          >
            Visits and leads over time
          </Typography>
          <PilotTimeSeriesChart
            data={overview?.timeSeries || []}
          />
        </Paper>
        <Paper variant="outlined" sx={{flex: 1, p: 2.5}}>
          <Typography
            variant="subtitle2"
            sx={{fontWeight: 600, mb: 2}}
          >
            Funnel
          </Typography>
          <PilotFunnelChart
            stages={(overview?.funnel || []).map((f: {stage: string; count: number}) => ({
              stage: f.stage,
              label: STAGE_LABELS[f.stage] || f.stage,
              count: f.count,
            }))}
          />
        </Paper>
      </Stack>

      <Stack direction={{xs: 'column', lg: 'row'}} spacing={2} sx={{mt: 2}}>
        <Paper variant="outlined" sx={{flex: 1, p: 2.5}}>
          <Typography
            variant="subtitle2"
            sx={{fontWeight: 600, mb: 1}}
          >
            By source
          </Typography>
          <PilotBreakdownChart
            data={overview?.sourceBreakdown || []}
            color="#3b82f6"
          />
        </Paper>
        <Paper variant="outlined" sx={{flex: 1, p: 2.5}}>
          <Typography
            variant="subtitle2"
            sx={{fontWeight: 600, mb: 1}}
          >
            By campaign (top 10)
          </Typography>
          <PilotBreakdownChart
            data={overview?.campaignBreakdown || []}
            color="#5f7a32"
          />
        </Paper>
      </Stack>
    </Box>
  );
}
